import React, { useEffect, useState } from 'react'
import { generatePath } from 'react-router-dom'
import { DataCard, DataColumn, DataGrid } from '@/components/DataCard/DataCard'
import {
  FormDateField,
  FormField,
  FormReadonlyField,
  FormSelectField,
  FormTextareaField,
} from '@/components/Form/FormField'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { zonedTimeToUtc } from '@/utils/time-zone/helpers'
import { ClientDto } from 'api/Clients/Types/Client'
import { BonusType } from 'api/Transaction/Types/BonusType'
import { CreateBonusTransaction } from 'api/Transaction/Types/CreateBonusTransaction'
import { RawTransactionDto } from 'api/Transaction/Types/RawTransactionDto'
import { BonusLabel } from 'api/Wallet/Types/BonusCampaignTypes'
import { WalletDto } from 'api/Wallet/Types/Wallet'
import { Link } from 'components/Link'
import { add } from 'date-fns'
import { Form, FormikErrors, FormikProps, withFormik } from 'formik'
import toNumber from 'lodash/toNumber'
import { CirclePlus } from 'lucide-react'
import { links } from 'utils/featureToggles'
import { FormSubmitValues } from 'utils/formValidation'

const BonusFormUI: React.FC<FormikProps<BonusFormValues> & OuterProps> = (props) => {
  const { client, handleSubmit, isSubmitting, values, errors, setFieldValue } = props
  const [isDepositTransactionsEnabled, setDepositTransactionsEnabled] = useState(false)
  const [formBonusType, setFormBonusType] = useState('')

  useEffect(() => {
    props.resetForm()
  }, [client?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (formBonusType) {
      setFieldValue('bonusTypeId', formBonusType)
    }
  }, [formBonusType, setFieldValue, values.bonusTypeId])

  useEffect(() => {
    const bonusType = props.bonusTypes.find((item) => item.id === values.bonusTypeId, 10)

    if (!bonusType) {
      return
    }

    let nextAmount = values.amount

    if (bonusType.baseType.id === 30 || bonusType.baseType.id === 40) {
      setDepositTransactionsEnabled(true)
    } else {
      setDepositTransactionsEnabled(false)
    }

    if (bonusType.amountType.id === 10) {
      setFieldValue('amount', bonusType.bonusAmount)
      nextAmount = bonusType.bonusAmount
    } else if (bonusType.amountType.id === 20) {
      const calculatedAmount = toNumber(
        (values.depositTransactionsAmount * bonusType.bonusAmount) / 100
      )
      setFieldValue('amount', calculatedAmount)
      nextAmount = calculatedAmount
    }

    if (bonusType.lotsRequiredType.id === 10) {
      setFieldValue('lotsRequired', bonusType.lotsRequired)
    } else if (bonusType.lotsRequiredType.id === 20) {
      setFieldValue('lotsRequired', toNumber((nextAmount * bonusType.lotsRequired) / 100))
    }

    if (bonusType.absoluteExpiryDate) {
      setFieldValue('expiration', bonusType.absoluteExpiryDate)
    } else {
      setFieldValue(
        'expiration',
        add(new Date(), { days: bonusType.relativeExpiryInDays }).toISOString()
      )
    }
  }, [values.bonusTypeId])

  const depositTransactionsLabel = (
    <span className='flex items-center gap-2'>
      Deposit Transactions
      {!!props.selectedTransactions.length && (
        <Tooltip>
          <TooltipTrigger>
            <CirclePlus className='size-4 text-green-600' />
          </TooltipTrigger>
          <TooltipContent side='right'>
            {props.selectedTransactions.length} transactions selected.{' '}
            {props.selectedTransactions.map((transaction) => transaction.referenceId)}. You can
            clear selected transactions by clicking the clear button.
          </TooltipContent>
        </Tooltip>
      )}
    </span>
  )

  return (
    <Form onSubmit={handleSubmit}>
      <DataGrid columns={1}>
        <DataColumn>
          <DataCard title='Bonus' columns={2}>
            <FormReadonlyField
              name='client'
              label='Client'
              value={values.client ?? ''}
              rightAddon={
                <>
                  <Button type='button' size='sm' variant='outline' onClick={props.setClient}>
                    Select
                  </Button>
                  {client?.id && (
                    <Link
                      to={generatePath(links.recordsClients.detailsUrl as string, {
                        id: client.id,
                        slug: 'details',
                      })}
                      showIcon
                    />
                  )}
                </>
              }
            />
            <FormSelectField
              name='walletId'
              label='Wallet'
              value={values.walletId || undefined}
              options={props.wallets.map((wallet) => ({
                id: wallet.id,
                name: `${wallet.name}-balance:${wallet.balance}`,
              }))}
              error={errors.walletId}
              onChange={(value) => {
                const nextWalletId = value ? String(value) : ''
                props.setFieldValue('walletId', nextWalletId)
                props.setWallet(nextWalletId)
              }}
              rightAddon={
                values.walletId && (
                  <Link
                    to={generatePath(links.recordsWallets.detailsUrl as string, {
                      id: values.walletId,
                      slug: 'details',
                    })}
                    showIcon
                  />
                )
              }
            />
            <FormSelectField
              name='bonusTypeId'
              label='Type'
              value={values.bonusTypeId || undefined}
              options={(props.bonusTypes ?? []).map((bonusType) => ({
                id: bonusType.id,
                name: bonusType.name,
              }))}
              error={errors.bonusTypeId}
              onChange={(value) => {
                const nextBonusTypeId = String(value)
                props.setFieldValue('bonusTypeId', nextBonusTypeId)
                setFormBonusType(nextBonusTypeId)
              }}
            />
            <FormSelectField
              name='bonusLabelId'
              label='Bonus Label'
              value={values.bonusLabelId || undefined}
              options={(props.bonusLabels || [])
                .filter((label) => label.id !== 0)
                .map((label) => ({
                  id: label.id,
                  name: label.name,
                }))}
              error={errors.bonusLabelId}
              onChange={(value) => props.setFieldValue('bonusLabelId', Number(value))}
            />
            <FormField
              name='depositTransactionsAmount'
              label={depositTransactionsLabel}
              value={
                isDepositTransactionsEnabled ? String(values.depositTransactionsAmount ?? '') : ''
              }
              readOnly
              disabled={!isDepositTransactionsEnabled}
              onClick={isDepositTransactionsEnabled ? props.onSelectTransactionModal : undefined}
              rightAddon={
                <Button
                  disabled={!isDepositTransactionsEnabled}
                  onClick={props.removeSelectedTransactions}
                  size='sm'
                  variant='outline'
                  type='button'
                >
                  Clear
                </Button>
              }
            />
            <FormField name='amount' label='Amount' type='number' error={errors.amount} />
            <FormField name='lotsRequired' label='Lots Required' type='number' />
            <FormDateField
              name='expiration'
              label='Expiry'
              dateFormatType={props.dateFormatType}
              onChange={(value) => setFieldValue('expiration', value?.toISOString() || null)}
              isClearable
            />
            <FormTextareaField name='comment' label='Comment' rows={3} maxLength={130} />
          </DataCard>
        </DataColumn>
      </DataGrid>
      <div className='mt-4 flex justify-center'>
        <Button disabled={isSubmitting} type='submit'>
          Submit
        </Button>
      </div>
    </Form>
  )
}

const dataToSubmit = (
  values: BonusFormValues,
  transactionIds: string[]
): FormSubmitValues<CreateBonusTransaction> => {
  return {
    transactions: [
      {
        amount: values.amount,
        walletId: values.walletId,
        bonusTypeId: values.bonusTypeId,
        expiryDate: zonedTimeToUtc(new Date(values.expiration)).toISOString(),
        lotsRequired: values.lotsRequired,
        transactionIds,
        bonusLabelId: values.bonusLabelId,
      },
    ],
    comment: values.comment,
  }
}

interface BonusFormValues {
  client: string | null
  walletId: string
  amount: number
  depositTransactionsAmount: number
  lotsRequired: number
  expiration: string
  comment: string
  bonusTypeId: string
  bonusLabelId: number
}

interface OuterProps {
  client: ClientDto | null
  wallets: WalletDto[]
  walletId: string
  bonusTypes: BonusType[]
  depositTransactionsAmount: number
  transactionIds: string[]
  selectedTransactions: RawTransactionDto[]
  bonusLabels: BonusLabel[]
  dateFormatType?: string

  onSubmit(values: FormSubmitValues<CreateBonusTransaction>): Promise<void>
  setClient(): void
  onSelectTransactionModal(): void
  removeSelectedTransactions(): void
  setWallet(walletId: string): void
}

export const BonusForm = withFormik<OuterProps, BonusFormValues>({
  mapPropsToValues: ({ client, depositTransactionsAmount, walletId }) => ({
    client: client?.name || null,
    walletId,
    depositTransactionsAmount,
    amount: 0,
    lotsRequired: 0,
    expiration: '',
    comment: '',
    bonusTypeId: '',
    bonusLabelId: 0,
  }),
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      await props.onSubmit(dataToSubmit(values, props.transactionIds))
    } finally {
      setSubmitting(false)
    }
  },
  validate: (values) => {
    const errors: FormikErrors<BonusFormValues> = {}

    if (!values.walletId) {
      errors.walletId = 'Required'
    }

    if (!values.amount) {
      errors.amount = 'Required'
    } else if (toNumber(values.amount) <= 0) {
      errors.amount = 'Must be greater than 0'
    }

    if (!values.bonusLabelId) {
      errors.bonusLabelId = 'Required'
    }

    return errors
  },
  enableReinitialize: true,
})(BonusFormUI)
